import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { useOrganization } from "@/context/OrganizationContext";
import { useAuth } from "@/context/AuthContext";
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Activity, FileText, GitBranch, Package, CheckCircle, MessageSquare } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { ContactPopup } from "@/components/contact/ContactPopup";

type DashboardStats = {
  diagramCount: number;
  componentCount: number;
  gitEventsCount: number;
};

export default function Dashboard() {
  const { currentOrganization } = useOrganization();
  const { user, session, subscriptionData } = useAuth();
  const [stats, setStats] = useState<DashboardStats>({
    diagramCount: 0,
    componentCount: 0,
    gitEventsCount: 0,
  });
  const [loading, setLoading] = useState(true);
  const [contactPopupOpen, setContactPopupOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const showDebug = searchParams.get('debug') === 'true';
  const showSuccess = searchParams.get('success') === 'true';

  useEffect(() => {
    if (currentOrganization?.id) {
      loadDashboardStats();
    }
  }, [currentOrganization]);

  // Remove success parameter from URL after showing the message
  useEffect(() => {
    if (showSuccess) {
      const timer = setTimeout(() => {
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.delete('success');
        setSearchParams(newSearchParams, { replace: true });
      }, 5000); // Remove after 5 seconds

      return () => clearTimeout(timer);
    }
  }, [showSuccess, searchParams, setSearchParams]);

  const loadDashboardStats = async () => {
    if (!currentOrganization?.id) return;

    setLoading(true);
    try {
      // Buscar quantidade de diagramas
      const { count: diagramCount } = await (supabase as any)
        .from("diagrams")
        .select("*", { count: 'exact', head: true })
        .eq("id_organization", currentOrganization.id);

      // Buscar quantidade de componentes
      const { count: componentCount } = await (supabase as any)
        .from("components")
        .select("*", { count: 'exact', head: true })
        .eq("id_organization", currentOrganization.id);

      // Para eventos git dos últimos 7 dias, vamos simular por enquanto
      // pois não temos uma tabela de eventos git no schema atual
      const gitEventsCount = Math.floor(Math.random() * 50); // Simulado

      setStats({
        diagramCount: diagramCount || 0,
        componentCount: componentCount || 0,
        gitEventsCount,
      });
    } catch (error) {
      console.error("Erro ao carregar estatísticas do dashboard:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Mensagem de Soft Launch */}
      <Alert className="border-blue-500 bg-blue-50 dark:bg-blue-950/20">
        <MessageSquare className="h-5 w-5 text-blue-600 dark:text-blue-400" />
        <AlertDescription className="text-blue-800 dark:text-blue-200">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <span className="text-sm font-medium">
              Estamos em soft launch até 31-Jul! Mozarch já está no ar e queremos muito ouvir você — nos conte sobre problemas, sugestões ou ideias para evoluirmos juntos.
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setContactPopupOpen(true)}
              className="shrink-0 border-blue-600 text-blue-700 hover:bg-blue-100 dark:border-blue-400 dark:text-blue-300 dark:hover:bg-blue-900/20"
            >
              <MessageSquare className="w-4 h-4 mr-1" />
              Fale Conosco
            </Button>
          </div>
        </AlertDescription>
      </Alert>

      {showSuccess && (
        <Alert className="border-green-500 bg-green-50 dark:bg-green-950/20">
          <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400" />
          <AlertDescription className="text-green-800 dark:text-green-200 font-medium text-base">
            Parabéns! Sua assinatura foi confirmada!
          </AlertDescription>
        </Alert>
      )}

      <div>
        <h1 className="text-3xl font-bold tracking-tight text-foreground">Dashboard</h1>
        <p className="text-muted-foreground">
          Visão geral da organização {currentOrganization?.name}
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        <Card className="border border-border/40 shadow-sm">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Diagramas</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {loading ? "..." : stats.diagramCount}
            </div>
            <p className="text-xs text-muted-foreground">
              Total de diagramas criados
            </p>
          </CardContent>
        </Card>

        <Card className="border border-border/40 shadow-sm">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Componentes</CardTitle>
            <Package className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {loading ? "..." : stats.componentCount}
            </div>
            <p className="text-xs text-muted-foreground">
              Total de componentes cadastrados
            </p>
          </CardContent>
        </Card>

        <Card className="border border-border/40 shadow-sm">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Eventos Git</CardTitle>
            <GitBranch className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {loading ? "..." : stats.gitEventsCount}
            </div>
            <p className="text-xs text-muted-foreground">
              Últimos 7 dias
            </p>
          </CardContent>
        </Card>
      </div>

      <Card className="border border-border/40 shadow-sm">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className="h-5 w-5" />
            Atividade Recente
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-sm text-muted-foreground">
            Nenhuma atividade recente encontrada.
          </div>
        </CardContent>
      </Card>

      {showDebug && (
        <Card className="border-2 border-red-500 bg-red-50 dark:bg-red-950/20">
          <CardHeader>
            <CardTitle className="text-red-700 dark:text-red-400">
              🐛 Debug Mode - Dados de Autenticação
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <h4 className="font-semibold text-red-700 dark:text-red-400">User Data:</h4>
              <pre className="text-xs bg-red-100 dark:bg-red-900/30 p-2 rounded overflow-auto">
                {JSON.stringify(user, null, 2)}
              </pre>
            </div>
            
            <div>
              <h4 className="font-semibold text-red-700 dark:text-red-400">Session Data:</h4>
              <pre className="text-xs bg-red-100 dark:bg-red-900/30 p-2 rounded overflow-auto">
                {JSON.stringify(session, null, 2)}
              </pre>
            </div>
            
            <div>
              <h4 className="font-semibold text-red-700 dark:text-red-400">Subscription Data:</h4>
              <pre className="text-xs bg-red-100 dark:bg-red-900/30 p-2 rounded overflow-auto">
                {JSON.stringify(subscriptionData, null, 2)}
              </pre>
            </div>
            
            <div>
              <h4 className="font-semibold text-red-700 dark:text-red-400">Current Organization:</h4>
              <pre className="text-xs bg-red-100 dark:bg-red-900/30 p-2 rounded overflow-auto">
                {JSON.stringify(currentOrganization, null, 2)}
              </pre>
            </div>
          </CardContent>
        </Card>
      )}

      <ContactPopup
        open={contactPopupOpen}
        onOpenChange={setContactPopupOpen}
      />
    </div>
  );
}
